import { debug } from '../utils/debug';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadLanguage } from '../i18n/config';

/**
 * Language Context
 *
 * Manages language state and provides translation utilities for dynamic content.
 * Works alongside i18next for fixed text translations.
 *
 * Fixed text (UI strings): Handled by i18next
 * Dynamic content (from database): Handled by this context
 *
 * IMPORTANT DESIGN PRINCIPLE: Avoid Data Redundancy
 * - Main tables contain DEFAULT language content
 * - Translation tables contain ONLY non-default languages
 * - Fallback chain: requested_lang (if != default) → default_lang (main table) → fallback
 *
 * NOT SHARED WITH clickandshare-frontend-v3.
 * This file began as a copy of the v3 context, but the two are independent and
 * deliberately incompatible: v3 mounts LanguageProvider under a HashRouter and
 * needs to read/write ?lang= inside the hash fragment, while this app uses
 * BrowserRouter (src/App.jsx) and uses window.location.search. Confirmed with
 * the v3 team — do not "re-sync" the two copies. Bug fixes that are genuinely
 * routing-independent are still worth porting by hand in either direction.
 */
const LanguageContext = createContext(null);

/**
 * Read the `lang` query parameter from the current URL.
 *
 * BrowserRouter keeps query params in window.location.search (a HashRouter app
 * would need to look inside the hash fragment instead — see the note above).
 *
 * The value is normalised defensively: URLs produced by the earlier
 * concatenation bug look like `?lang=de?lang=en`, which parses as the single
 * value "de?lang=en". Taking the segment before any stray `?`/`&` recovers a
 * usable code from links users have already bookmarked; anything still
 * unrecognised is rejected by the caller's supportedLanguages check.
 */
const readLangFromUrl = () => {
  const raw = new URLSearchParams(window.location.search).get('lang');
  if (!raw) return null;
  return raw.split(/[?&]/)[0].trim().toLowerCase() || null;
};

const STORAGE_KEY = 'preferredLanguage';

/**
 * localStorage access that degrades gracefully.
 *
 * Ported by hand from clickandshare-frontend-v3 (09f90cd). Android WebViews
 * with DOM storage disabled throw on localStorage access rather than returning
 * null. readStoredLanguage() is called from the useState initialiser below, so
 * an unguarded throw takes down the whole LanguageProvider subtree — the entire
 * manage page, not just the language switcher.
 */
const readStoredLanguage = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const writeStoredLanguage = (lang) => {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch (e) {
    debug.warn('Unable to persist language preference: storage unavailable');
  }
};

/**
 * Language Provider Component
 *
 * Provides language state and translation utilities for dynamic content.
 *
 * @param {object} merchantData - Merchant configuration with supported languages
 * @param {string} merchantData.default_language - Default language code (e.g., 'en')
 * @param {array} merchantData.supported_languages - Array of supported language codes
 */
export const LanguageProvider = ({ merchantData, children }) => {
  const { i18n } = useTranslation();

  // Get initial language with priority: URL > localStorage > browser > merchant default
  const getInitialLanguage = () => {
    const supportedLanguages = merchantData?.supported_languages || ['en'];

    // 1. Check URL parameter (highest priority - explicit user intent).
    //    This is a ONE-SHOT override: it decides the language for this page
    //    load but is never written back to localStorage. A ?lang= we did not
    //    author (an emailed link carrying the booking's language, say) must not
    //    permanently overwrite a preference the customer set for themselves via
    //    the language switcher. Only changeLanguage() persists.
    const urlLang = readLangFromUrl();
    if (urlLang && supportedLanguages.includes(urlLang)) {
      return urlLang;
    }

    // 2. Check localStorage (user's previous selection)
    const storedLang = readStoredLanguage();
    if (storedLang && supportedLanguages.includes(storedLang)) {
      return storedLang;
    }

    // 3. Check browser language (user's browser setting)
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang) {
      // Extract language code (e.g., 'de' from 'de-DE')
      const langCode = browserLang.split('-')[0].toLowerCase();
      if (supportedLanguages.includes(langCode)) {
        return langCode;
      }
    }

    // 4. Use merchant default or fallback to 'en'
    return merchantData?.default_language || 'en';
  };

  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage());

  // Sync i18next language with current language, loading translations on demand
  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      loadLanguage(currentLanguage).then(() => {
        i18n.changeLanguage(currentLanguage);
      });
    }
  }, [currentLanguage, i18n]);

  // Sync HTML lang attribute for WCAG 3.1.1 (Language of Page)
  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Keep the ?lang= query parameter in sync with the active language.
  //
  // Mutating a URL object REPLACES the parameter. The previous implementation
  // built a query string from the (always empty, under BrowserRouter) hash and
  // concatenated it onto window.location.search, which already held the old
  // ?lang= — so every switch appended another one and the URL grew without
  // bound: /b/<token>?lang=de?lang=en?lang=de...
  //
  // The comparison is against the URL's actual value rather than a "last
  // synced" ref, so the parameter is also restored if something else rewrites
  // the URL and drops it. replaceState does not notify React Router or fire
  // popstate, so this cannot feed back into a render loop.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('lang') === currentLanguage) return;

    url.searchParams.set('lang', currentLanguage);
    window.history.replaceState({}, '', url);
  }, [currentLanguage]);

  /**
   * Change current language
   * @param {string} lang - Language code (e.g., 'en', 'de')
   */
  const changeLanguage = (lang) => {
    const supportedLanguages = merchantData?.supported_languages || ['en'];
    if (supportedLanguages.includes(lang)) {
      setCurrentLanguage(lang);
      // Store in localStorage for persistence. Only an explicit switch persists
      // — see the one-shot note on the URL check in getInitialLanguage.
      writeStoredLanguage(lang);
    } else {
      debug.warn(`Language '${lang}' is not supported by this merchant`);
    }
  };

  /**
   * Get merchant-specific translation from merchant_translations table data
   *
   * Fallback logic (NO REDUNDANCY):
   * 1. If current language == default language → use mainTableValue (default language in main table)
   * 2. Else try translation table for current language
   * 3. If not found → use mainTableValue (fallback to default language)
   *
   * @param {string} mainTableValue - Value from main table (in default language)
   * @param {array} merchantTranslations - Array of translation objects from database (non-default languages only)
   * @param {string} field - Field name to translate (e.g., 'business_name', 'description')
   * @returns {string} Translated text
   */
  const getMerchantTranslation = (mainTableValue, merchantTranslations, field) => {
    const defaultLang = merchantData?.default_language || 'en';

    // If requesting default language, use main table (no need to check translations)
    if (currentLanguage === defaultLang) {
      return mainTableValue || '';
    }

    // Try to find translation for requested language (non-default)
    if (merchantTranslations && Array.isArray(merchantTranslations)) {
      const translation = merchantTranslations.find(
        (t) => t.language_code === currentLanguage
      );

      if (translation && translation[field]) {
        return translation[field];
      }
    }

    // Fallback to main table (default language)
    return mainTableValue || '';
  };

  /**
   * Get product type translation from product_type_translations table data
   *
   * @param {string} mainTableValue - Value from main table (in default language)
   * @param {array} productTranslations - Array of translation objects from database (non-default languages only)
   * @param {string} field - Field name to translate
   * @returns {string} Translated text
   */
  const getProductTranslation = (mainTableValue, productTranslations, field) => {
    const defaultLang = merchantData?.default_language || 'en';

    // If requesting default language, use main table
    if (currentLanguage === defaultLang) {
      return mainTableValue || '';
    }

    // Try to find translation for requested language (non-default)
    if (productTranslations && Array.isArray(productTranslations)) {
      const translation = productTranslations.find(
        (t) => t.language_code === currentLanguage
      );

      if (translation && translation[field]) {
        return translation[field];
      }
    }

    // Fallback to main table (default language)
    return mainTableValue || '';
  };

  /**
   * Get pricing rule translation
   *
   * @param {string} mainTableValue - Duration label from main table (in default language)
   * @param {array} pricingTranslations - Array of translation objects from database (non-default languages only)
   * @returns {string} Translated duration label
   */
  const getPricingTranslation = (mainTableValue, pricingTranslations) => {
    const defaultLang = merchantData?.default_language || 'en';

    // If requesting default language, use main table
    if (currentLanguage === defaultLang) {
      return mainTableValue || '';
    }

    // Try to find translation for requested language (non-default)
    if (pricingTranslations && Array.isArray(pricingTranslations)) {
      const translation = pricingTranslations.find(
        (t) => t.language_code === currentLanguage
      );

      if (translation && translation.duration_label) {
        return translation.duration_label;
      }
    }

    // Fallback to main table (default language)
    return mainTableValue || '';
  };

  /**
   * Get location translation from location_translations table data
   *
   * @param {string} mainTableValue - Value from main table (in default language)
   * @param {array} locationTranslations - Array of translation objects from database (non-default languages only)
   * @param {string} field - Field name to translate (e.g., 'location_name')
   * @returns {string} Translated text
   */
  const getLocationTranslation = (mainTableValue, locationTranslations, field) => {
    const defaultLang = merchantData?.default_language || 'en';

    // If requesting default language, use main table
    if (currentLanguage === defaultLang) {
      return mainTableValue || '';
    }

    // Try to find translation for requested language (non-default)
    if (locationTranslations && Array.isArray(locationTranslations)) {
      const translation = locationTranslations.find(
        (t) => t.language_code === currentLanguage
      );

      if (translation && translation[field]) {
        return translation[field];
      }
    }

    // Fallback to main table (default language)
    return mainTableValue || '';
  };

  /**
   * Get inventory item translation from inventory_item_translations table data
   *
   * @param {string} mainTableValue - Value from main table (in default language)
   * @param {array} inventoryTranslations - Array of translation objects from database (non-default languages only)
   * @param {string} field - Field name to translate (e.g., 'inventory_specific_info')
   * @returns {string} Translated text
   */
  const getInventoryTranslation = (mainTableValue, inventoryTranslations, field) => {
    const defaultLang = merchantData?.default_language || 'en';

    // If requesting default language, use main table
    if (currentLanguage === defaultLang) {
      return mainTableValue || '';
    }

    // Try to find translation for requested language (non-default)
    if (inventoryTranslations && Array.isArray(inventoryTranslations)) {
      const translation = inventoryTranslations.find(
        (t) => t.language_code === currentLanguage
      );

      if (translation && translation[field]) {
        return translation[field];
      }
    }

    // Fallback to main table (default language)
    return mainTableValue || '';
  };

  const value = {
    // Current language state
    currentLanguage,
    changeLanguage,

    // Merchant configuration
    supportedLanguages: merchantData?.supported_languages || ['en'],
    defaultLanguage: merchantData?.default_language || 'en',

    // Translation utilities for dynamic content (no redundancy)
    // All functions now require mainTableValue + translations array
    getMerchantTranslation,
    getProductTranslation,
    getPricingTranslation,
    getLocationTranslation,
    getInventoryTranslation,

    // Raw merchant data for advanced use
    merchantData,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook to access language context
 *
 * @returns {object} Language context with translation functions
 * @throws {Error} If used outside LanguageProvider
 *
 * @example
 * const { currentLanguage, getProductTranslation, getLocationTranslation, getInventoryTranslation } = useLanguage();
 *
 * // Product translation
 * // product.product_name contains default language (from main table)
 * // product.translations contains non-default languages (from translation table)
 * const productName = getProductTranslation(
 *   product.product_name,          // Default language from main table
 *   product.translations,           // Non-default languages from translation table
 *   'product_name'                  // Field name
 * );
 *
 * // Location translation
 * const locationName = getLocationTranslation(
 *   location.location_name,         // Default language from main table
 *   location.translations,          // Non-default languages from translation table
 *   'location_name'                 // Field name
 * );
 *
 * // Inventory item translation
 * const inventoryInfo = getInventoryTranslation(
 *   item.inventory_specific_info,   // Default language from main table
 *   item.translations,              // Non-default languages from translation table
 *   'inventory_specific_info'       // Field name
 * );
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
};

/**
 * HOC to provide language context to a component
 */
export const withLanguage = (Component) => {
  return (props) => {
    const language = useLanguage();
    return <Component {...props} language={language} />;
  };
};

export default LanguageContext;
